import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import type { FlowWindow, Trade, WalletTier } from "../api/types";
import { Empty, Panel } from "../components/Panel";
import {
  Counterparty,
  PolymarketProfile,
  ProfileName,
} from "../components/Tier";
import { useCounterparties } from "../hooks/useCounterparties";
import { useTrades } from "../hooks/useTrades";
import {
  CP_PERIODS,
  counterpartiesSummary,
  counterpartiesWarnings,
  counterpartyLines,
  marketLabel,
  type CounterpartyLine,
} from "../lib/counterparties";
import { formatTime, plural, shortId } from "../lib/format";
import { FLOW_WINDOWS, flowWindowLabel } from "../lib/participants";
import { isMaker, ourFill, ourOutcome, ourSide, tradeKey } from "../lib/trades";
import { useFlowWindow } from "../store/useFlowWindow";
import { useTiers } from "../store/useTiers";

/**
 * Кто принимает наши ставки.
 *
 * # Зачем отдельная вкладка, когда есть «Мои сделки»
 *
 * Потому что там строка на СДЕЛКУ, а вопрос задают о КОШЕЛЬКЕ: «я накликал тысячу
 * тейкером — её принял один бот или тридцать разных, и кто из них стоит против
 * меня каждый день». Свести первое во второе глазами нельзя: страница истории
 * обрезана двумя сотнями сделок, а один кошелёк встречается в десятках из них.
 *
 * # Зачем это оператору
 *
 * Чтобы понимать силу сопротивления. Есть контрагенты, которые приняли налив и в
 * рынок больше не пошли, — им можно налить и спокойно ждать экспирации. Есть
 * те, кто тут же возвращается защищать позицию, — с ними надо переставляться
 * быстро. Первый признак различия виден уже здесь: колонка «снял нас» это те, кто
 * пришёл САМ, а «принял наш налив» — те, кто просто стоял в стакане.
 *
 * Что кошелёк делал ПОСЛЕ конкретной встречи, показывает панель накопления на
 * странице рынка в режиме «против нас»: там есть свод рынка со временем сделок,
 * которого в журнале нет.
 */
export function CounterpartiesPage() {
  const [params, setParams] = useSearchParams();
  const market = params.get("market") ?? undefined;
  const sinceHours = Number(params.get("hours") ?? 24) || 24;
  // Окно статистики коротких рынков — общее с панелью накопления: это одни и те
  // же четыре колонки про один и тот же кошелёк, и два независимых выбора
  // означали бы разный PnL у него на двух экранах.
  const picked = useFlowWindow((s) => s.picked);
  const pick = useFlowWindow((s) => s.pick);

  const query = useCounterparties({ sinceHours, market, flow: picked });
  const p = query.data;
  const lines = counterpartyLines(p);
  const warnings = counterpartiesWarnings(p);
  const tiers = useTiers((s) => s.tiers);

  const setFilter = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <Panel
      title="Контрагенты"
      subtitle={counterpartiesSummary(p) || undefined}
      actions={
        <Period
          hours={sinceHours}
          onChange={(h) => setFilter("hours", String(h))}
        />
      }
    >
      {market && (
        <button
          onClick={() => setFilter("market", undefined)}
          className="mb-2 text-xs text-[var(--color-accent)] hover:underline"
          title="снять фильтр по рынку"
        >
          рынок {shortId(market)} ✕
        </button>
      )}

      {warnings.length > 0 && (
        <div className="mb-3 space-y-1">
          {warnings.map((w) => (
            // Словами, а не пустым местом: пустота на месте строки читается как
            // «таких встреч не было», а это другой факт.
            <p key={w} className="text-xs text-amber-300">
              {w}
            </p>
          ))}
        </div>
      )}

      {query.isLoading && !p ? (
        <Empty>загрузка…</Empty>
      ) : query.error ? (
        <p className="text-sm text-[var(--color-sell)]">
          {(query.error as Error).message}
        </p>
      ) : lines.length === 0 ? (
        <Empty>
          За выбранный период встреч не было. Свод наполняется ИСПОЛНЕНИЯМИ: в
          него попадает то, что действительно свелось, а не выставленные ордера.
        </Empty>
      ) : (
        <CounterpartiesTable
          lines={lines}
          tiers={tiers}
          sinceHours={sinceHours}
          window={p?.flow_window}
          picked={picked}
          onPickWindow={pick}
        />
      )}
    </Panel>
  );
}

function Period({
  hours,
  onChange,
}: {
  hours: number;
  onChange: (hours: number) => void;
}) {
  return (
    <div className="flex items-center gap-1 text-xs">
      {CP_PERIODS.map((p) => (
        <button
          key={p.label}
          onClick={() => onChange(p.hours)}
          className={`rounded px-2 py-0.5 ${
            p.hours === hours
              ? "bg-[var(--color-accent)]/15 text-[var(--color-accent)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function CounterpartiesTable({
  lines,
  tiers,
  sinceHours,
  window: shown,
  picked,
  onPickWindow,
}: {
  lines: CounterpartyLine[];
  tiers: Readonly<Record<string, WalletTier>>;
  sinceHours: number;
  window: FlowWindow | undefined;
  picked: FlowWindow | undefined;
  onPickWindow: (w: FlowWindow) => void;
}) {
  // Раскрытая строка показывает конкретные встречи. В свёрнутом виде их нет
  // вовсе, и это правильно: свод отвечает «кто и сколько», а «когда именно» —
  // вопрос, который задают к ОДНОЙ строке, а не ко всем сразу.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (wallet: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(wallet)) next.add(wallet);
      return next;
    });

  const active = picked ?? shown;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          {/* Группирующая строка — не украшение: левые девять колонок описывают
              НАШИ встречи с кошельком, следующие четыре — его торговлю вообще, и
              без разделителя «встреч 12» и «ставок 412» в одной строке выглядят
              противоречием, а не двумя ответами. «Последняя встреча» стоит вне
              групп: она снова про встречи, и накрывать её баннером чужой
              статистики значило бы подписать число не тем вопросом. Та же
              раскладка, что в панели накопления. */}
          <tr className="text-left text-[var(--color-muted)]">
            <th className="pb-1 font-normal" colSpan={9}>
              <span className="text-[10px] uppercase tracking-wide">
                встречи с нами
              </span>
            </th>
            <th
              className="border-l border-[var(--color-border)] pb-1 pl-3 font-normal"
              colSpan={4}
              title={
                "статистика кошелька на КОРОТКИХ рынках вообще, а не в наших сделках: " +
                "считается по его собственной истории за выбранное окно — и только по " +
                "тем рынкам, которые успели разрешиться"
              }
            >
              <span className="flex items-baseline gap-2">
                <span className="text-[10px] uppercase tracking-wide">
                  на коротких рынках
                </span>
                <span className="flex items-baseline gap-1">
                  {FLOW_WINDOWS.map((w) => (
                    <button
                      key={w.value}
                      type="button"
                      onClick={() => onPickWindow(w.value)}
                      title={w.title}
                      className={`rounded px-1 text-[10px] normal-case ${
                        w.value === active
                          ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
                          : "text-[var(--color-muted)] hover:text-white"
                      }`}
                    >
                      {w.label}
                    </button>
                  ))}
                  {/* Выбор уже сменился, а числа ещё за прежнее окно — это видно
                      словами, а не догадкой по мигнувшей таблице. */}
                  {picked && shown && picked !== shown && (
                    <span className="text-[10px] normal-case text-amber-300">
                      показано за {flowWindowLabel(shown)}…
                    </span>
                  )}
                </span>
              </span>
            </th>
            {/* Пустая ячейка над «Последней встречей»: колонка не входит ни в
                одну группу, и без неё строки шапки разъехались бы на колонку. */}
            <th className="pb-1 font-normal" />
          </tr>
          <tr className="text-left text-[var(--color-muted)]">
            <th className="pb-2 font-normal">Кошелёк</th>
            <th
              className="pb-2 font-normal"
              title={
                "как кошелёк подписан на самой бирже. Пусто — имени мы не знаем: у части " +
                "кошельков его нет вовсе, а до остальных мог не дойти обход профилей. " +
                "По имени открывается его страница на Polymarket"
              }
            >
              Имя
            </th>
            <th
              className="pb-2 text-right font-normal"
              title="набрано СТОЯНИЕМ В СТАКАНЕ: мы били, он принимал. Такой контрагент пришёл не сам"
            >
              Принял наш налив
            </th>
            <th
              className="pb-2 text-right font-normal"
              title={
                "набрано АГРЕССИЕЙ: мы стояли в стакане, он нас снял. Такой контрагент " +
                "пришёл САМ — он и защищать позицию будет активнее. Размер здесь НАШ: " +
                "сколько агрессор взял всего, по нашему филлу не видно"
              }
            >
              Снял нас
            </th>
            <th
              className="pb-2 text-right font-normal"
              title="обе половины в долларах — по этому числу таблица и отсортирована"
            >
              Всего, $
            </th>
            <th
              className="pb-2 text-right font-normal"
              title={
                "средняя цена, по которой он забирал у нас: нотионал, делённый на шеры. " +
                "Число грубое — половины считаются на разных токенах"
              }
            >
              Ср. цена
            </th>
            <th
              className="pb-2 text-right font-normal"
              title="наших сделок, разделённых с ним. Не встречных строк: их бывает две на одну сделку"
            >
              Встреч
            </th>
            <th
              className="pb-2 text-right font-normal"
              title={
                "по скольким рынкам мы его встречали. Тот же объём в одном окне и в " +
                "тридцати говорит разное: первое случайность, второе — постоянный оппонент"
              }
            >
              Рынков
            </th>
            <th
              className="pb-2 pr-4 text-right font-normal"
              title={
                "НАШ результат встреч с ним: сколько мы на них выиграли или проиграли. " +
                "Считается по погашению окна, а не по текущей цене — короткий рынок " +
                "гасится ровно в 0 или 1, поэтому у закрытого окна это точное число. " +
                "Встреча на таком рынке — игра с нулевой суммой: наш плюс здесь и есть " +
                "его минус"
              }
            >
              Наш итог
            </th>
            <th
              className="border-l border-[var(--color-border)] pb-2 pl-3 text-right font-normal"
              title="сколько сделок кошелёк совершил на коротких рынках за глубину наблюдения"
            >
              Ставок
            </th>
            <th
              className="pb-2 text-right font-normal"
              title={
                "прибыль на коротких рынках за вычетом тейкерской комиссии. На профиле " +
                "Polymarket то же число будет больше ровно на неё — биржа комиссию из " +
                "прибыли не вычитает"
              }
            >
              пнл
            </th>
            <th
              className="pb-2 text-right font-normal"
              title="РОЙ: прибыль на доллар, занесённый в рынок покупками"
            >
              РОЙ
            </th>
            {/* Выплаты — то, из-за чего «наш плюс = его минус» перестаёт быть
                правдой. Возврат комиссии платит третья сторона, и обоим сразу:
                оппонент, проигравший нам на сделках и получивший больше того
                возвратом, не проигравший. */}
            <th
              className="pb-2 text-right font-normal"
              title={
                "сколько ему ДОПЛАТИЛИ помимо сделок: возврат комиссии тейкеру и " +
                "мейкеру, награды. В пнл слева это не входит. Здесь у колонки особый " +
                "смысл: встреча с нами — игра с нулевой суммой, но выплату платит " +
                "третья сторона, и с ней его минус бывает плюсом. Прочерк — выплат " +
                "не видели: не спрашивали либо переводов за окно не было"
              }
            >
              Выплаты
            </th>
            <th className="pb-2 text-right font-normal">Последняя встреча</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <Row
              key={line.wallet}
              line={line}
              tiers={tiers}
              sinceHours={sinceHours}
              expanded={open.has(line.wallet)}
              onToggle={() => toggle(line.wallet)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Row({
  line,
  tiers,
  sinceHours,
  expanded,
  onToggle,
}: {
  line: CounterpartyLine;
  tiers: Readonly<Record<string, WalletTier>>;
  sinceHours: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="cursor-pointer border-t border-[var(--color-border)] hover:bg-white/5"
        title="раскрыть конкретные встречи"
      >
        <td className="py-1 pr-3">
          <span onClick={(e) => e.stopPropagation()}>
            {/* Тот же компонент, что в ленте и в панели: ссылка на нашу страницу
                кошелька плюс значок квалификации. Поток profile_update свежее
                ответа, но приходит только при СМЕНЕ тира, поэтому основание —
                поле строки. */}
            <Counterparty
              address={line.wallet}
              tier={tiers[line.wallet.toLowerCase()] ?? line.tier}
              hideEmptyTier
            />
          </span>
        </td>
        {/* Имя обычным цветом, а не приглушённым: приглушённое читается как
            второстепенное, а имя здесь — ровно то, за что цепляется глаз среди
            шестнадцатеричных строк. Пусто у тех, кто не подписан ни именем, ни
            псевдонимом, — выдумывать там нечего.

            Ссылка на биржу стоит ЗДЕСЬ, а не у адреса: обе ведут на один
            профиль Polymarket, и место им рядом. У адреса она разрывала бы
            пару «адрес — наша страница кошелька», а здесь достраивает пару
            «имя — профиль, где это имя и написано». */}
        <td className="py-1 pr-3" onClick={(e) => e.stopPropagation()}>
          <span className="inline-flex max-w-[16ch] items-center gap-1.5">
            <span className="truncate">
              <ProfileName wallet={line.wallet} name={line.name} />
            </span>
            <PolymarketProfile address={line.wallet} />
          </span>
        </td>
        {/* Прочерк, а не «0.0»: ноль читался бы как «встречался этой ролью и взял
            ничего», а на деле такой роли между нами не было вовсе. */}
        <td
          className="num py-1 pr-3 text-right"
          title={line.tookUSD ?? undefined}
        >
          {line.took ?? <span className="text-[var(--color-muted)]">—</span>}
        </td>
        <td
          className="num py-1 pr-3 text-right"
          title={line.hitUSD ?? undefined}
          // Агрессор выделен: это он приходит сам, и это он будет защищать позицию.
          style={line.hit ? { color: "var(--color-warn)" } : undefined}
        >
          {line.hit ?? <span className="text-[var(--color-muted)]">—</span>}
        </td>
        <td className="num py-1 pr-3 text-right">{line.totalUSD}</td>
        <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">
          {line.avgPrice ?? "—"}
        </td>
        <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">
          {line.trades}
        </td>
        <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">
          {line.markets}
        </td>
        {/* Прочерк, а не «$0.00»: ноль читался бы как «сыграли вничью», а на
            деле ни одно окно с ним ещё не погасилось — исхода просто нет. */}
        <td
          className="num py-1 pr-4 text-right"
          style={{ color: pnlColor(line.ourPnl) }}
          title={scoreTitle(line)}
        >
          {line.ourPnl ?? <span className="text-[var(--color-muted)]">—</span>}
          {line.unsettled > 0 && line.settled > 0 && (
            // Глубина рядом с суммой, как у PnL коротких рынков: «+$12» по трём
            // встречам из тридцати иначе читается как итог всех тридцати.
            <span className="ml-1 text-[10px] text-[var(--color-muted)]">
              ·{line.settled}/{line.settled + line.unsettled}
            </span>
          )}
        </td>
        <td
          className="num border-l border-[var(--color-border)] py-1 pl-3 pr-3 text-right"
          title={depthTitle(line)}
        >
          {line.bets ?? <span className="text-[var(--color-muted)]">—</span>}
        </td>
        <td
          className="num py-1 pr-3 text-right"
          style={{ color: pnlColor(line.pnl) }}
          title={depthTitle(line)}
        >
          {line.pnl ?? <span className="text-[var(--color-muted)]">—</span>}
          {line.depthLabel && (
            <span className="ml-1 text-[10px] text-[var(--color-muted)]">
              ·{line.depthLabel}
              {line.partial && "*"}
            </span>
          )}
        </td>
        <td
          className="num py-1 pr-3 text-right"
          style={{ color: pnlColor(line.roi) }}
          title={depthTitle(line)}
        >
          {line.roi ?? <span className="text-[var(--color-muted)]">—</span>}
        </td>
        <td
          className="num py-1 pr-3 text-right"
          style={{ color: pnlColor(line.paid) }}
          title={line.paidTitle}
        >
          {line.paid ?? <span className="text-[var(--color-muted)]">—</span>}
        </td>
        <td className="num py-1 text-right text-[var(--color-muted)]">
          {formatTime(line.lastAt)}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-[var(--color-border)] bg-black/20">
          <td colSpan={14} className="px-3 py-2">
            <Meetings wallet={line.wallet} sinceHours={sinceHours} />
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Конкретные встречи с кошельком — те самые сделки, из которых сложился свод.
 *
 * Читается УЖЕ СУЩЕСТВУЮЩИМ маршрутом истории с фильтром по кошельку: он умеет и
 * встречные строки, и агрессора (`EXISTS(trade_fills) OR taker_address`). Второй
 * эндпоинт под тот же вопрос означал бы два места, где решается, что считать
 * встречей.
 *
 * Запрос уходит ТОЛЬКО при раскрытии строки: сотня раскрытых строк — это сотня
 * запросов, и делать их заранее ради ответа, который спрашивают у одной, незачем.
 */
function Meetings({
  wallet,
  sinceHours,
}: {
  wallet: string;
  sinceHours: number;
}) {
  const query = useTrades({ wallet, sinceHours });
  const trades = query.data?.trades ?? [];

  if (query.isLoading && !query.data) {
    return <p className="text-xs text-[var(--color-muted)]">загрузка…</p>;
  }
  if (query.error) {
    return (
      <p className="text-xs text-[var(--color-sell)]">
        {(query.error as Error).message}
      </p>
    );
  }
  if (trades.length === 0) {
    return (
      <p className="text-xs text-[var(--color-muted)]">
        Сделок с этим кошельком за период не нашлось. Такое бывает, когда свод
        читал более длинное окно, чем открытая история.
      </p>
    );
  }

  return (
    <table className="w-full text-xs">
      {/* Шапка обязательна, хотя строк тут единицы: без неё «0.56» и «5» стоят
          рядом без ответа на вопрос, что это. Раньше её не было, и раскрытая
          строка читалась как набор чисел. */}
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
          <th className="pb-1 pr-3 font-normal">время</th>
          <th className="pb-1 pr-3 font-normal">окно</th>
          <th
            className="pb-1 pr-3 font-normal"
            title="кто из нас пришёл в стакан сам"
          >
            кто пришёл
          </th>
          <th className="pb-1 pr-3 font-normal">наша сторона</th>
          <th className="pb-1 pr-3 text-right font-normal">цена</th>
          <th className="pb-1 pr-3 text-right font-normal">размер</th>
          <th className="pb-1 font-normal" />
        </tr>
      </thead>
      <tbody>
        {trades.map((t) => (
          <Meeting key={tradeKey(t)} trade={t} wallet={wallet} />
        ))}
      </tbody>
    </table>
  );
}

function Meeting({ trade, wallet }: { trade: Trade; wallet: string }) {
  const fill = ourFill(trade);
  const side = ourSide(trade);
  const outcome = ourOutcome(trade);
  const color = side === "BUY" ? "var(--color-buy)" : "var(--color-sell)";
  // Роль называется НАША: «мейкер» здесь значит «он снял нас», и без этой
  // подписи размер в соседней ячейке повисает без ответа на вопрос, кто пришёл.
  const maker = isMaker(trade);

  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="num py-0.5 pr-3 text-[var(--color-muted)]">
        {formatTime(trade.timestamp)}
      </td>
      {/* Окно человеческим именем, а не идентификатором: `0x6108d59a…` не
          говорит ни про актив, ни про время, а вопрос к списку встреч ровно
          такой. Идентификатор остаётся в подсказке — по нему ищут в журнале. */}
      <td className="py-0.5 pr-3">
        <Link
          to={`/market/${encodeURIComponent(trade.condition_id)}`}
          className="text-[var(--color-muted)] hover:text-[var(--color-accent)] hover:underline"
          title={`открыть рынок\n${trade.condition_id}`}
        >
          {marketLabel(trade.slug, trade.condition_id)}
        </Link>
      </td>
      <td
        className="py-0.5 pr-3 text-[var(--color-muted)]"
        title={
          maker
            ? "мы стояли в стакане, он нас снял — пришёл сам"
            : "мы били, он принимал — стоял в стакане"
        }
      >
        {maker ? "он снял нас" : "принял наш налив"}
      </td>
      <td className="py-0.5 pr-3" style={{ color }}>
        {side === "BUY" ? "покупка" : "продажа"}
        {outcome && (
          <span className="ml-1 text-[var(--color-muted)]">{outcome}</span>
        )}
      </td>
      <td className="num py-0.5 pr-3 text-right" style={{ color }}>
        {fill?.price ?? "—"}
      </td>
      <td className="num py-0.5 pr-3 text-right">{fill?.size ?? "—"}</td>
      <td className="py-0.5 text-right text-[var(--color-muted)]">
        <Link
          to={`/trades?wallet=${encodeURIComponent(wallet)}`}
          className="hover:text-[var(--color-accent)] hover:underline"
          title="открыть эту сделку в истории со всеми встречными строками"
        >
          разбор
        </Link>
      </td>
    </tr>
  );
}

/**
 * Подсказка о глубине наблюдения — та же, что в панели накопления.
 *
 * Говорится словами, а не числом рядом: «за 1 сут» и «за 7 сут» это разная
 * степень доверия к тем же цифрам, и у нового кошелька глубина ровно одни сутки,
 * потому что глубже история не влезает в потолок источника.
 */
function depthTitle(line: CounterpartyLine): string {
  if (line.depthDays === 0) {
    return (
      "кошелька ещё не обходили: либо его оборот ниже порога наблюдения, " +
      "либо очередь до него не дошла. Это не «наторговал ноль»"
    );
  }
  const base = `по ${line.depthDays} сут наблюдений на коротких рынках — фактическая глубина свода`;
  const parts = [base];
  if (line.partial) {
    parts.push(
      "Часть суток покрыта не целиком (сегодняшние ещё идут либо история " +
        "кошелька плотнее одного обхода), и числа за них занижены",
    );
  }
  // Непригодные сутки — другой диагноз, чем неполные: те занижены, а эти
  // выброшены целиком, потому что выборки истории разошлись и данным за них
  // верить нельзя.
  if (line.unusableDays > 0) {
    parts.push(
      `${line.unusableDays} сут записаны прочерком: пнл с ролью за них ` +
        "выброшены, а в ставки и оборот они входят — выборки истории разошлись",
    );
  }
  return parts.join(". ");
}

/**
 * Что стоит за нашим итогом встреч — словами.
 *
 * Главное здесь не сумма, а её основание: сумма без глубины выглядит итогом всех
 * встреч, а непогашенных окон на пятиминутных рынках больше всего у самых свежих
 * — то есть ровно у тех, ради которых вкладку и открывают.
 */
function scoreTitle(line: CounterpartyLine): string {
  if (line.settled === 0) {
    return (
      "результат ещё не посчитан: ни одно из окон, где мы с ним встречались, " +
      "не погасилось либо их исход не подтверждён. Это не «сыграли вничью»"
    );
  }
  const base =
    `${line.record} из ${line.settled} ` +
    `${plural(line.settled, "погашенной встречи", "погашенных встреч", "погашенных встреч")}. ` +
    "Считается по исходу окна: купленное по 0.30 и погасившееся в единицу дало " +
    "70 центов на шер, минус наша комиссия";
  if (line.unsettled === 0) return base;
  return (
    `${base}. Ещё ${line.unsettled} ` +
    `${plural(line.unsettled, "встреча ждёт", "встречи ждут", "встреч ждут")} исхода ` +
    "и в сумму не входят"
  );
}

/**
 * Цвет знаковой величины по ПРЕФИКСУ строки: она уже собрана в lib с
 * типографским минусом, и второй разбор был бы вторым местом, где решается знак.
 */
function pnlColor(value: string | null): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("+")) return "var(--color-buy)";
  if (value.startsWith("−")) return "var(--color-sell)";
  return undefined;
}
